import { useEffect } from "react";

function isVisible(el) {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    style.opacity !== "0"
  );
}

function shouldSkipEscape() {
  const selectMenu = document.querySelector(".select__menu");
  if (selectMenu && isVisible(selectMenu)) return true;

  if (
    document.querySelector(
      ".ant-select-dropdown:not(.ant-select-dropdown-hidden)"
    )
  ) {
    return true;
  }

  if (document.querySelector(".flatpickr-calendar.open")) return true;
  if (document.querySelector(".react-datepicker-popper")) return true;

  const autocompletePopper = document.querySelector(".MuiAutocomplete-popper");
  if (autocompletePopper && isVisible(autocompletePopper)) return true;

  return false;
}

function getVisibleModals() {
  return [
    ...document.querySelectorAll(
      ".modal.fade.show, .modal.show, .modal-overlay"
    ),
  ].filter(isVisible);
}

function getModalPanel(modal) {
  return (
    modal.querySelector(".modal-dialog") ||
    modal.querySelector(".modal-container") ||
    modal.querySelector(".modal-content")
  );
}

function isClickOnModalBackdrop(event, modal) {
  const target = event.target;
  if (!(target instanceof Element)) return false;

  const panel = getModalPanel(modal);
  if (panel) {
    return modal.contains(target) && !panel.contains(target);
  }

  return target === modal;
}

function getTopmostModal(modals) {
  if (!modals.length) return null;
  if (modals.length === 1) return modals[0];

  return [...modals].sort((a, b) => {
    const zA = parseInt(window.getComputedStyle(a).zIndex, 10) || 0;
    const zB = parseInt(window.getComputedStyle(b).zIndex, 10) || 0;
    if (zB !== zA) return zB - zA;
    return 0;
  })[0];
}

function hasOpenSuggestionsInModal(modal) {
  const lists = modal.querySelectorAll(
    '[class*="uggestion"], [class*="mention"], .list-group'
  );

  for (const list of lists) {
    if (!isVisible(list)) continue;
    if (
      list.querySelector(
        "li, [role='option'], .list-group-item, .suggestion-item"
      )
    ) {
      return true;
    }
  }

  return false;
}

function findModalCloseButton(modal) {
  const header = modal.querySelector(
    ".modalBorder, .modal-header, .setting-header"
  );
  const scopedRoot = header && isVisible(header) ? header : modal;

  const closeSvg = scopedRoot.querySelector(
    'svg[data-testid="CloseSharpIcon"], svg[data-testid="CloseIcon"]'
  );
  if (closeSvg) {
    return closeSvg.closest(".pointer, button, [role='button']") || closeSvg;
  }

  const btnClose = modal.querySelector(".btn-close");
  if (btnClose) return btnClose;

  if (header && isVisible(header)) {
    const headerIcons = [
      ...header.querySelectorAll(".pointer, .MuiSvgIcon-root"),
    ];
    const iconLike = headerIcons.filter(
      (el) =>
        el.tagName === "svg" ||
        el.querySelector("svg") ||
        el.classList.contains("MuiSvgIcon-root")
    );

    if (iconLike.length) return iconLike[iconLike.length - 1];
  }

  const closeSvgAnywhere = modal.querySelector(
    'svg[data-testid="CloseSharpIcon"], svg[data-testid="CloseIcon"]'
  );
  if (closeSvgAnywhere) {
    return (
      closeSvgAnywhere.closest(".pointer, button, [role='button']") ||
      closeSvgAnywhere
    );
  }

  return null;
}

function triggerElementClick(element) {
  if (!element || typeof element.dispatchEvent !== "function") return false;

  if (typeof element.click === "function") {
    element.click();
    return true;
  }

  // SVG elements (MUI CloseSharpIcon) don't expose .click() — dispatch instead.
  element.dispatchEvent(
    new MouseEvent("click", { bubbles: true, cancelable: true, view: window })
  );
  return true;
}

function findClickableCloseTarget(modal) {
  const closeButton = findModalCloseButton(modal);
  if (!closeButton) return null;

  if (typeof closeButton.click === "function") return closeButton;

  const clickableParent = closeButton.closest(
    ".pointer, button, [role='button'], [onclick]"
  );
  if (clickableParent && typeof clickableParent.click === "function") {
    return clickableParent;
  }

  return closeButton;
}

function closeTopmostModal(event) {
  if (shouldSkipEscape()) return false;

  const modals = getVisibleModals();
  if (!modals.length) return false;

  const topModal = getTopmostModal(modals);
  if (!topModal || topModal.dataset.modalNoEscape === "true") return false;
  if (topModal.dataset.bsBackdrop === "static") return false;
  if (hasOpenSuggestionsInModal(topModal)) return false;

  const closeButton = findClickableCloseTarget(topModal);
  if (!closeButton) return false;

  event.preventDefault();
  event.stopImmediatePropagation();
  triggerElementClick(closeButton);
  return true;
}

/**
 * Global Escape + overlay-click handler for custom div modals across the app.
 * Clicks the header close button on the topmost visible modal — same as pressing X.
 */
export default function useGlobalModalEscape() {
  useEffect(() => {
    let ignoreBackdropClick = false;

    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      closeTopmostModal(e);
    };

    const handleMouseDown = (e) => {
      if (e.button !== 0) return;

      const modals = getVisibleModals();
      const topModal = getTopmostModal(modals);
      if (!topModal) {
        ignoreBackdropClick = false;
        return;
      }

      const panel = getModalPanel(topModal);
      ignoreBackdropClick = !!(panel && panel.contains(e.target));
    };

    const handleClick = (e) => {
      if (e.button !== 0) return;

      if (ignoreBackdropClick) {
        ignoreBackdropClick = false;
        return;
      }

      const modals = getVisibleModals();
      const topModal = getTopmostModal(modals);
      if (!topModal || !isClickOnModalBackdrop(e, topModal)) return;

      closeTopmostModal(e);
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);
}
